import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Clock, CheckCircle, XCircle, ArrowRight, ArrowLeft, BookOpen, Play, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MCQExamPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('categories'); // categories, exam, results
  const [categories, setCategories] = useState({});
  const [examData, setExamData] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/mcq/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(res.data.counts || {});
    } catch (err) { console.error(err); }
  };

  const startExam = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mcq/start-exam`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setExamData(res.data);
      setTimeLeft(res.data.time_limit_minutes * 60);
      setAnswers({});
      setCurrentQ(0);
      setPhase('exam');
      toast.success('Examination started. Good luck!');
    } catch (err) {
      toast.error('Failed to start exam');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (phase === 'exam' && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoading(true);
    try {
      const answersList = examData.questions.map(q => ({
        question_id: q.id,
        selected_answer: answers[q.id] || ''
      }));
      const res = await axios.post(`${API}/mcq/submit-exam`, {
        exam_id: examData.exam_id,
        answers: answersList
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(res.data);
      setPhase('results');
    } catch (err) {
      toast.error('Failed to submit exam');
    } finally { setLoading(false); }
  }, [examData, answers, token]);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // Categories Phase
  if (phase === 'categories') {
    return (
      <div data-testid="mcq-page">
        <section className="bg-[#1B2A4A] py-16 md:py-20">
          <div className="imcac-container">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:bg-white/10 mb-4 -ml-2"
              data-testid="mcq-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
            <p className="imcac-overline mb-3">Written Examination</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">MCQ Examination</h1>
            <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          </div>
        </section>
        <section className="imcac-section bg-[#F8FAFC]">
          <div className="imcac-container max-w-4xl">
            <Card className="imcac-card mb-8">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-4">Examination Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-[#F8FAFC] rounded-sm border border-slate-100 text-center">
                    <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{Object.values(categories).reduce((a, b) => a + b, 0)}</p>
                    <p className="text-xs text-slate-500">Questions in Bank</p>
                  </div>
                  <div className="p-4 bg-[#F8FAFC] rounded-sm border border-slate-100 text-center">
                    <p className="text-2xl font-bold text-[#1B2A4A] font-heading">60 min</p>
                    <p className="text-xs text-slate-500">Time Limit</p>
                  </div>
                  <div className="p-4 bg-[#F8FAFC] rounded-sm border border-slate-100 text-center">
                    <p className="text-2xl font-bold text-[#1B2A4A] font-heading">60%</p>
                    <p className="text-xs text-slate-500">Pass Mark</p>
                  </div>
                </div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#C5A572] mb-3">Question Categories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {Object.entries(categories).map(([cat, count]) => (
                    <div key={cat} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-sm">
                      <span className="text-sm text-slate-700">{cat}</span>
                      <Badge variant="outline" className="text-xs rounded-sm">{count} Qs</Badge>
                    </div>
                  ))}
                </div>
                <Button onClick={startExam} disabled={loading} className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm w-full py-3" data-testid="start-exam-btn">
                  <Play className="h-4 w-4 mr-2" /> {loading ? 'Starting...' : 'Start Examination'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // Exam Phase
  if (phase === 'exam' && examData) {
    const q = examData.questions[currentQ];
    const answeredCount = Object.keys(answers).length;
    return (
      <div className="bg-[#F8FAFC] min-h-screen" data-testid="mcq-exam-active">
        <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
          <div className="imcac-container py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className="bg-[#1B2A4A] text-white rounded-sm">Q {currentQ + 1}/{examData.questions.length}</Badge>
              <Progress value={answeredCount / examData.questions.length * 100} className="w-32 h-2 bg-slate-200 [&>div]:bg-[#C5A572]" />
              <span className="text-xs text-slate-500">{answeredCount} answered</span>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-1.5 text-sm font-mono font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-[#1B2A4A]'}`}>
                <Clock className="h-4 w-4" /> {formatTime(timeLeft)}
              </div>
              <Button onClick={handleSubmit} disabled={loading} variant="outline" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 text-sm" data-testid="submit-exam-btn">
                {loading ? 'Submitting...' : 'Submit Exam'}
              </Button>
            </div>
          </div>
        </div>

        <div className="imcac-container max-w-3xl py-8">
          <Card className="imcac-card" data-testid={`question-${currentQ}`}>
            <CardContent className="p-6 md:p-8">
              <Badge className="bg-[#F1F5F9] text-slate-600 text-xs rounded-sm mb-3">{q.category}</Badge>
              <h2 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-6 leading-relaxed">{q.question}</h2>
              <div className="space-y-3">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => setAnswers({ ...answers, [q.id]: opt })}
                    className={`w-full text-left p-4 rounded-sm border transition-all text-sm ${
                      answers[q.id] === opt
                        ? 'border-[#1B2A4A] bg-[#1B2A4A]/5 text-[#1B2A4A] font-medium'
                        : 'border-slate-200 hover:border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`option-${i}`}
                  >
                    <span className="inline-block w-6 h-6 mr-3 text-center leading-6 rounded-sm text-xs font-bold bg-[#F1F5F9] border border-slate-200">
                      {String.fromCharCode(65 + i)}
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} className="rounded-sm" data-testid="prev-question-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Previous
            </Button>
            {currentQ < examData.questions.length - 1 ? (
              <Button onClick={() => setCurrentQ(currentQ + 1)} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="next-question-btn">
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm" data-testid="finish-exam-btn">
                {loading ? 'Submitting...' : 'Finish Exam'}
              </Button>
            )}
          </div>

          {/* Question Navigator */}
          <Card className="imcac-card mt-6">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Question Navigator</p>
              <div className="flex flex-wrap gap-2">
                {examData.questions.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentQ(i)}
                    className={`w-8 h-8 rounded-sm text-xs font-medium transition-colors ${
                      i === currentQ
                        ? 'bg-[#1B2A4A] text-white'
                        : answers[examData.questions[i].id]
                          ? 'bg-[#C5A572]/20 text-[#C5A572] border border-[#C5A572]/30'
                          : 'bg-[#F1F5F9] text-slate-500 border border-slate-200'
                    }`}
                    data-testid={`nav-q-${i}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Results Phase
  if (phase === 'results' && results) {
    return (
      <div data-testid="mcq-results">
        <section className="bg-[#1B2A4A] py-16 md:py-20">
          <div className="imcac-container text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:bg-white/10 mb-4"
              data-testid="mcq-results-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
            <p className="imcac-overline mb-3">Examination Complete</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
              {results.passed ? 'Congratulations!' : 'Examination Results'}
            </h1>
          </div>
        </section>
        <section className="imcac-section bg-[#F8FAFC]">
          <div className="imcac-container max-w-3xl">
            <Card className="imcac-card mb-8">
              <CardContent className="p-8 text-center">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${results.passed ? 'bg-green-100' : 'bg-red-100'}`}>
                  {results.passed ? <CheckCircle className="h-10 w-10 text-green-600" /> : <XCircle className="h-10 w-10 text-red-600" />}
                </div>
                <p className="text-5xl font-bold text-[#1B2A4A] font-heading mb-2">{results.score}%</p>
                <Badge className={`text-sm rounded-sm px-4 py-1 ${results.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid="exam-result-badge">
                  {results.passed ? 'PASSED' : 'NOT PASSED'}
                </Badge>
                <p className="text-sm text-slate-500 mt-4">{results.correct} of {results.total} questions answered correctly (Pass mark: {results.pass_mark}%)</p>
              </CardContent>
            </Card>

            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Answer Review</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {results.results.map((r, i) => (
                    <div key={i} className={`p-4 rounded-sm border ${r.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-start gap-2">
                        {r.is_correct ? <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5" />}
                        <div className="flex-1">
                          <p className="text-sm text-slate-700 font-medium">{r.question}</p>
                          <p className="text-xs text-slate-500 mt-1">Your answer: <span className={r.is_correct ? 'text-green-700' : 'text-red-700'}>{r.selected || '(unanswered)'}</span></p>
                          {!r.is_correct && <p className="text-xs text-green-700 mt-0.5">Correct: {r.correct}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center gap-4 mt-8">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-sm" data-testid="results-dashboard-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
              </Button>
              <Button onClick={() => { setPhase('categories'); setResults(null); setExamData(null); }} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="retake-exam-btn">
                <BookOpen className="h-4 w-4 mr-2" /> Take Another Exam
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return null;
}
